import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Edit2, Save, X, Plus, Trash2, ChevronDown, ChevronUp, FileText, Code } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import AdminLayout from '../../components/admin/AdminLayout';
import { toast } from 'react-toastify';

interface CmsSection {
  id: string;
  name: string;
  slug: string;
  description: string;
}

interface CmsContent {
  id: string;
  section_id: string;
  key: string;
  value: any;
  type: 'text' | 'number' | 'image' | 'html' | 'counter';
  label: string;
}

function CMSManagement() {
  const [sections, setSections] = useState<CmsSection[]>([]);
  const [contents, setContents] = useState<CmsContent[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [editingContent, setEditingContent] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<Record<string, any>>({});
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const [newContent, setNewContent] = useState({
    key: '',
    label: '',
    value: '',
    type: 'text' as 'text' | 'number' | 'image' | 'html' | 'counter'
  });
  const [showNewContentForm, setShowNewContentForm] = useState(false);
  
  const navigate = useNavigate();

  useEffect(() => {
    fetchSections();
  }, []);

  useEffect(() => {
    if (activeSection) {
      fetchContents(activeSection);
    }
  }, [activeSection]);

  const fetchSections = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('cms_sections')
        .select('*')
        .order('name');

      if (error) throw error;
      
      setSections(data || []);
      
      // Se ci sono sezioni, imposta la prima come attiva
      if (data && data.length > 0) {
        setActiveSection(data[0].id);
        
        // Inizializza le sezioni espanse
        const expanded: Record<string, boolean> = {};
        data.forEach(section => {
          expanded[section.id] = true; // Tutte le sezioni inizialmente espanse
        });
        setExpandedSections(expanded);
      }
    } catch (error: any) {
      console.error('Error fetching CMS sections:', error);
      toast.error(`Errore: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchContents = async (sectionId: string) => {
    try {
      const { data, error } = await supabase
        .from('cms_content')
        .select('*')
        .eq('section_id', sectionId)
        .order('label');

      if (error) throw error;
      setContents(data || []);
    } catch (error: any) {
      console.error('Error fetching CMS contents:', error);
      toast.error(`Errore: ${error.message}`);
    }
  };

  const handleEditClick = (content: CmsContent) => {
    setEditingContent(content.id);
    setEditValues({ ...editValues, [content.id]: content.value });
  };

  const handleCancelEdit = () => {
    setEditingContent(null);
  };

  const handleValueChange = (contentId: string, value: any) => {
    setEditValues({ ...editValues, [contentId]: value });
  };

  const handleSaveContent = async (content: CmsContent) => {
    try {
      const newValue = editValues[content.id];
      
      const { error } = await supabase
        .from('cms_content')
        .update({ value: newValue })
        .eq('id', content.id);

      if (error) throw error;
      
      // Aggiorna lo stato locale
      setContents(contents.map(c => 
        c.id === content.id ? { ...c, value: newValue } : c
      ));
      
      setEditingContent(null);
      toast.success('Contenuto aggiornato con successo');
    } catch (error: any) {
      console.error('Error updating CMS content:', error);
      toast.error(`Errore: ${error.message}`);
    }
  };

  const handleSectionClick = (sectionId: string) => {
    setActiveSection(sectionId);
  };

  const toggleSectionExpand = (sectionId: string) => {
    setExpandedSections({
      ...expandedSections,
      [sectionId]: !expandedSections[sectionId]
    });
  };

  const handleNewContentChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setNewContent(prev => ({ ...prev, [name]: value }));
  };

  const handleAddContent = async () => {
    if (!activeSection || !newContent.key || !newContent.label) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }

    try {
      const { data, error } = await supabase
        .from('cms_content')
        .insert({
          section_id: activeSection,
          key: newContent.key,
          label: newContent.label,
          value: newContent.value,
          type: newContent.type
        })
        .select()
        .single();

      if (error) throw error;
      
      // Aggiorna lo stato locale
      setContents([...contents, data]);
      
      // Resetta il form
      setNewContent({
        key: '',
        label: '',
        value: '',
        type: 'text'
      });
      
      setShowNewContentForm(false);
      toast.success('Contenuto aggiunto con successo');
    } catch (error: any) {
      console.error('Error adding CMS content:', error);
      toast.error(`Errore: ${error.message}`);
    }
  };

  const handleDeleteContent = async (contentId: string) => {
    if (!confirm('Sei sicuro di voler eliminare questo contenuto?')) return;
    
    try {
      const { error } = await supabase
        .from('cms_content')
        .delete()
        .eq('id', contentId);

      if (error) throw error;
      
      // Aggiorna lo stato locale
      setContents(contents.filter(c => c.id !== contentId));
      toast.success('Contenuto eliminato con successo');
    } catch (error: any) {
      console.error('Error deleting CMS content:', error);
      toast.error(`Errore: ${error.message}`);
    }
  };

  const renderContentEditor = (content: CmsContent) => {
    const isEditing = editingContent === content.id;
    const value = isEditing ? editValues[content.id] : content.value;
    
    let displayValue = value;
    
    // Parsing JSON values if needed
    if (typeof value === 'string') {
      try {
        if (value.startsWith('"') || value.startsWith('{') || value.startsWith('[')) {
          const parsed = JSON.parse(value);
          if (typeof parsed === 'string') {
            displayValue = parsed;
          }
        }
      } catch (e) {
        // Ignora errori di parsing, usa il valore originale
      }
    }

    return (
      <div className="p-4 bg-white rounded-lg shadow-sm mb-4">
        <div className="flex justify-between items-start mb-3">
          <div>
            <h4 className="font-medium text-gray-800">{content.label}</h4>
            <p className="text-xs text-gray-500">{content.key}</p>
          </div>
          <div className="flex items-center">
            {content.type === 'counter' && (
              <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-md text-xs font-medium mr-2">
                Contatore
              </span>
            )}
            {content.type === 'html' && (
              <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-md text-xs font-medium mr-2">
                HTML
              </span>
            )}
            {content.type === 'image' && (
              <span className="px-2 py-1 bg-green-100 text-green-700 rounded-md text-xs font-medium mr-2">
                Immagine
              </span>
            )}
            {isEditing ? (
              <div className="flex space-x-2">
                <button
                  onClick={() => handleSaveContent(content)}
                  className="p-1 text-green-600 hover:text-green-800"
                  title="Salva"
                >
                  <Save className="h-5 w-5" />
                </button>
                <button
                  onClick={handleCancelEdit}
                  className="p-1 text-red-600 hover:text-red-800"
                  title="Annulla"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            ) : (
              <div className="flex space-x-2">
                <button
                  onClick={() => handleEditClick(content)}
                  className="p-1 text-blue-600 hover:text-blue-800"
                  title="Modifica"
                >
                  <Edit2 className="h-5 w-5" />
                </button>
                <button
                  onClick={() => handleDeleteContent(content.id)}
                  className="p-1 text-red-600 hover:text-red-800"
                  title="Elimina"
                >
                  <Trash2 className="h-5 w-5" />
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="mt-2">
          {isEditing ? (
            <>
              {content.type === 'counter' || content.type === 'number' ? (
                <input
                  type="number"
                  value={value}
                  onChange={(e) => handleValueChange(content.id, e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary focus:border-primary"
                />
              ) : content.type === 'html' ? (
                <textarea
                  value={value}
                  onChange={(e) => handleValueChange(content.id, e.target.value)}
                  rows={5}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary focus:border-primary"
                />
              ) : (
                <input
                  type="text"
                  value={value}
                  onChange={(e) => handleValueChange(content.id, e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary focus:border-primary"
                />
              )}
            </>
          ) : (
            <>
              {content.type === 'counter' ? (
                <div className="bg-gray-50 p-3 rounded-md font-mono text-lg font-medium">
                  {displayValue}
                </div>
              ) : content.type === 'html' ? (
                <div className="bg-gray-50 p-3 rounded-md overflow-hidden max-h-32 text-sm">
                  <pre className="whitespace-pre-wrap">{displayValue}</pre>
                </div>
              ) : (
                <div className="bg-gray-50 p-3 rounded-md">
                  {displayValue}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  const renderNewContentForm = () => {
    if (!showNewContentForm) return null;

    return (
      <div className="p-4 bg-white rounded-lg shadow-sm mb-6 border-2 border-primary">
        <h4 className="font-medium text-primary mb-4">Aggiungi Nuovo Contenuto</h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Chiave* <span className="text-xs text-gray-500">(identificatore univoco)</span>
            </label>
            <input
              type="text"
              name="key"
              value={newContent.key}
              onChange={handleNewContentChange}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary focus:border-primary"
              placeholder="es. anno_fondazione"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Etichetta* <span className="text-xs text-gray-500">(nome visualizzato)</span>
            </label>
            <input
              type="text"
              name="label"
              value={newContent.label}
              onChange={handleNewContentChange}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary focus:border-primary"
              placeholder="es. Anno di Fondazione"
            />
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tipo di Contenuto*
            </label>
            <select
              name="type"
              value={newContent.type}
              onChange={handleNewContentChange}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary focus:border-primary"
            >
              <option value="text">Testo</option>
              <option value="number">Numero</option>
              <option value="counter">Contatore</option>
              <option value="html">HTML</option>
              <option value="image">Immagine</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Valore
            </label>
            {newContent.type === 'html' ? (
              <textarea
                name="value"
                value={newContent.value}
                onChange={handleNewContentChange}
                rows={3}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary focus:border-primary"
                placeholder="Inserisci il contenuto HTML..."
              />
            ) : newContent.type === 'counter' || newContent.type === 'number' ? (
              <input
                type="number"
                name="value"
                value={newContent.value}
                onChange={handleNewContentChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary focus:border-primary"
                placeholder="es. 2017"
              />
            ) : (
              <input
                type="text"
                name="value"
                value={newContent.value}
                onChange={handleNewContentChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary focus:border-primary"
                placeholder="Inserisci il valore..."
              />
            )}
          </div>
        </div>
        
        <div className="flex justify-end space-x-2 mt-4">
          <button
            type="button"
            onClick={() => setShowNewContentForm(false)}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Annulla
          </button>
          <button
            type="button"
            onClick={handleAddContent}
            className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-light transition-colors"
          >
            Aggiungi
          </button>
        </div>
      </div>
    );
  };

  return (
    <AdminLayout title="Gestione CMS">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold">Gestione Contenuti</h2>
          <p className="text-gray-600">Gestisci i contenuti dinamici del sito web</p>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
          <p className="mt-2 text-gray-600">Caricamento contenuti...</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Sidebar con le sezioni */}
          <div className="md:col-span-1">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold mb-4 pb-2 border-b">Sezioni</h3>
              <nav className="space-y-2">
                {sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => handleSectionClick(section.id)}
                    className={`w-full text-left py-2 px-3 rounded-lg transition-colors ${
                      activeSection === section.id
                        ? 'bg-primary text-white'
                        : 'hover:bg-gray-100 text-gray-700'
                    }`}
                  >
                    {section.name}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Contenuti della sezione selezionata */}
          <div className="md:col-span-3">
            {activeSection && (
              <div>
                <div className="bg-white rounded-lg shadow-sm p-4 md:p-6 mb-4">
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                    <h3 className="text-lg font-semibold mb-2">
                      {sections.find((s) => s.id === activeSection)?.name}
                    </h3>
                    <button
                      onClick={() => setShowNewContentForm(!showNewContentForm)}
                      className="px-3 py-1 bg-primary text-white rounded-md hover:bg-primary-light transition-colors flex items-center text-sm"
                    >
                      {showNewContentForm ? (
                        <>
                          <X className="h-4 w-4 mr-1" />
                          Annulla
                        </>
                      ) : (
                        <>
                          <Plus className="h-4 w-4 mr-1" />
                          Aggiungi Contenuto
                        </>
                      )}
                    </button>
                  </div>
                  <p className="text-gray-600">
                    {sections.find((s) => s.id === activeSection)?.description}
                  </p>
                </div>

                {renderNewContentForm()}

                <div className="space-y-4">
                  {contents.length === 0 ? (
                    <div className="bg-white p-8 text-center rounded-lg shadow-sm">
                      <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-gray-700 mb-2">Nessun contenuto trovato</h3>
                      <p className="text-gray-500 mb-6">
                        Questa sezione non ha ancora contenuti. Aggiungi il primo contenuto.
                      </p>
                      <button
                        onClick={() => setShowNewContentForm(true)}
                        className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-light transition-colors inline-flex items-center"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Aggiungi Contenuto
                      </button>
                    </div>
                  ) : (
                    <div>
                      {contents.map((content) => renderContentEditor(content))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default CMSManagement;